import React, { useState, useEffect, useRef, ReactNode, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { LoadingIndicator } from '@atlassian/temp-loading-indicator';
import { getUserProfile, getUserRedirectionData, getUIConfigData } from '@atlassian/temp-proxies';
import { AuthenticatedUserProfile } from '@atlassian/cssxp-typescommon';
import { ApplicationContext } from './application-context';
import { getUserRoles } from '../../role-mapper';

interface UserProfileState {
  loading: boolean;
  userProfile: AuthenticatedUserProfile | null;
}

export const DefaultApplicationContext = ({ children, type }: { children: ReactNode; type?: string }) => {
  const [userRedirectionData, setUserRedirectionData] = useState<any>();
  const [userProfileState, setUserProfileState] = useState<UserProfileState>({
    loading: true,
    userProfile: null,
  });
  const [uiConfig, setUIConfig] = useState<any>();
  const [timeZone, setTimeZone] = useState<string>('');
  const history = useHistory();
  const isMounted = useRef(true);
  useEffect(() => {
    // Execute
    if (isMounted.current) {
      fetchUserProfile();
      fetchUIConfig();
    } else {
      setUserProfileState({
        ...userProfileState,
        loading: false,
      });
      setUIConfig(null);
    }
    // Clean  up
    return () => {
      isMounted.current = false;
    };
    async function fetchUserProfile() {
      try {
        const contextMenuData = await getUserRedirectionData();
        const user = await getUserProfile();
        setUserRedirectionData(contextMenuData);
        setUserProfileState({
          loading: false,
          userProfile: user,
        });
      } catch (e: any) {
        setUserProfileState({
          loading: false,
          userProfile: null,
        });
        if (e.statusCode === 401) {
          history.push('/unauthorized');
        } else {
          history.push('/error');
        }
      }
    }
    async function fetchUIConfig() {
      try {
        const uiConfig = await getUIConfigData();
        setUIConfig(uiConfig);
      } catch (e: any) {
        setUIConfig(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const userRoles = useMemo(() => {
    if (userProfileState?.userProfile?.principals) {
      return getUserRoles(userProfileState.userProfile.principals);
    }
    return [];
  }, [userProfileState]);

  if (userProfileState.loading) {
    return <LoadingIndicator />;
  }
  return (
    <ApplicationContext.Provider
      // @ts-ignore
      value={{
        ...userProfileState.userProfile,
        ...{ redirectionData: userRedirectionData },
        roles: userRoles,
        product: type,
        uiConfig: uiConfig,
        timeZone: timeZone,
        setTimeZone,
      }}
    >
      {children}
    </ApplicationContext.Provider>
  );
};
